import React, { useState } from 'react';
import { X, FileText, Phone, Mail, CheckCircle2, AlertTriangle, ShieldAlert, UploadCloud, Bot, Loader2, Key } from 'lucide-react';
import { useCRM } from '../context/CRMContext';
import { useAuth, ROLES } from '../context/AuthContext';
import LeadModal from './LeadModal';

const ClientDrawer = ({ client, onClose }) => {
  const { user } = useAuth();
  const { logActivity } = useCRM();
  
  const [isUploading, setIsUploading] = useState(false);
  const [aiError, setAiError] = useState(null); // null, 'scanning', 'error', 'approved'
  const [forceApproved, setForceApproved] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  if (!client) return null;

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleUploadPolicy(e.target.files[0].name);
    }
  };

  const handleUploadPolicy = (fileName) => {
    setIsUploading(true);
    setAiError('scanning');
    
    // Simulate File Upload + GenAI Processing
    setTimeout(() => {
      setIsUploading(false);
      setAiError('error');
      logActivity(`Intento de validación de PDF (${fileName}) para ${client.clientName}. Motor IA detectó anomalías.`);
    }, 3500);
  };

  const handleForceApprove = () => {
    setForceApproved(true);
    setAiError('approved');
    logActivity(`El Validador (${user.name}) aprobó forzosamente la póliza de ${client.clientName} omitiendo la advertencia IA.`);
  };

  return (
    <>
      {/* Overlay */}
      <div 
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 100,
          backdropFilter: 'blur(2px)'
        }}
        onClick={onClose}
      />

      {/* Drawer */}
      <div 
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0,
          width: '100%', maxWidth: '450px', backgroundColor: 'var(--surface)',
          zIndex: 101, boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column',
          transform: 'translateX(0)', transition: 'transform 0.3s ease-out',
          animation: 'slideInRight 0.3s ease-out forwards'
        }}
      >
        <style>
          {`
            @keyframes slideInRight {
              from { transform: translateX(100%); }
              to { transform: translateX(0); }
            }
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
            .spin-anim { animation: spin 1s linear infinite; }
          `}
        </style>

        {/* Header */}
        <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'var(--secondary)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div className="avatar" style={{ width: 48, height: 48, fontSize: '1.25rem' }}>
              {client.clientName.charAt(0)}
            </div>
            <div>
              <h2 style={{ fontSize: '1.25rem', marginBottom: '0.2rem' }}>{client.clientName}</h2>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'flex', gap: '0.5rem' }}>
                ID: {client.id}
              </span>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
            <X size={24} />
          </button>
        </div>

        {/* Content Tabs */}
        <div style={{ padding: '1.5rem', flex: 1, overflowY: 'auto' }}>
          
          <h3 style={{ fontSize: '1rem', color: 'var(--text-muted)', marginBottom: '1rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Información Central</h3>
          
          <div className="card" style={{ marginBottom: '2rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <Phone size={18} color="var(--primary)" />
              <span style={{ fontWeight: 500 }}>{client.phone}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <Mail size={18} color="var(--primary)" />
              <span style={{ fontWeight: 500, color: 'var(--info)' }}>{client.email || client.contact_email || 'Sin correo asociado'}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', borderTop: '1px dashed var(--border)', paddingTop: '1rem' }}>
              <div style={{ flex: 1 }}>
                <p className="text-muted" style={{ fontSize: '0.75rem', marginBottom: '0.25rem' }}>Tipo Seguro</p>
                <p style={{ fontWeight: 600 }}>{client.type}</p>
              </div>
              <div style={{ flex: 1 }}>
                <p className="text-muted" style={{ fontSize: '0.75rem', marginBottom: '0.25rem' }}>Valor/Prima</p>
                <p style={{ fontWeight: 600, color: 'var(--primary)', display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
                  <span>{client.value}</span>
                  {client.value_uf && (
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Eq. {client.value_uf} UF</span>
                  )}
                </p>
              </div>
            </div>
          </div>

          <h3 style={{ fontSize: '1rem', color: 'var(--text-muted)', marginBottom: '1rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Expediente & Validación AI</h3>
          
          {/* Uploader AI Zone */}
          <div style={{ backgroundColor: 'var(--bg-color)', border: '1px dashed var(--primary)', borderRadius: '8px', padding: '1.5rem', textAlign: 'center', marginBottom: '1.5rem' }}>
            {aiError === null && (
              <>
                <UploadCloud size={32} color="var(--primary)" style={{ margin: '0 auto 1rem auto' }} />
                <p style={{ fontSize: '0.9rem', marginBottom: '1rem' }}>Sube el contrato final. La IA de Veritas lo escaneará automáticamente.</p>
                <label className="btn" style={{ margin: '0 auto', backgroundColor: 'white', border: '1px solid var(--border)', cursor: 'pointer', display: 'inline-block' }}>
                  Seleccionar PDF
                  <input type="file" accept=".pdf" onChange={handleFileChange} style={{ display: 'none' }} />
                </label>
              </>
            )}

            {aiError === 'scanning' && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
                <Loader2 size={32} color="var(--primary)" className="spin-anim" />
                <p style={{ fontSize: '0.9rem', color: 'var(--primary)', fontWeight: 600 }}>Motor IA analizando firmas y metadatos...</p>
              </div>
            )}

            {aiError === 'error' && (
              <div style={{ textAlign: 'left', backgroundColor: 'var(--danger-bg)', padding: '1rem', borderRadius: '6px', border: '1px solid var(--danger)' }}>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', color: 'var(--danger)', marginBottom: '0.5rem' }}>
                  <Bot size={20} />
                  <span style={{ fontWeight: 600 }}>Anomalía Detectada por IA</span>
                </div>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-main)', marginBottom: '1rem' }}>
                  El valor de la prima en el PDF no coincide con los <strong>{client.value}</strong> registrados en el sistema, y falta la firma del contratante en la página 3.
                </p>
                
                {/* Botón de Override solo para Validador / Admin */}
                {(user?.role === ROLES.VALIDATOR || user?.role === ROLES.ADMIN) ? (
                  <button onClick={handleForceApprove} className="btn" style={{ width: '100%', justifyContent: 'center', backgroundColor: 'white', color: 'var(--danger)', border: '1px solid var(--danger)' }}>
                    <Key size={16} /> Forzar Aprobación (Riesgo Asumido)
                  </button>
                ) : (
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic', margin: 0 }}>
                    <AlertTriangle size={14} style={{ display: 'inline', verticalAlign: 'middle' }} /> Contacte a un Validador para desbloquear esta póliza.
                  </p>
                )}
              </div>
            )}

            {aiError === 'approved' && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', color: 'var(--success)', fontWeight: 600 }}>
                <CheckCircle2 size={24} />
                <span>Póliza Validada Exitosamente</span>
              </div>
            )}
          </div>

          <h3 style={{ fontSize: '1rem', color: 'var(--text-muted)', marginBottom: '1rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Siniestros Históricos</h3>
          <div style={{ padding: '1rem', backgroundColor: 'var(--secondary)', borderRadius: '6px', display: 'flex', gap: '1rem' }}>
            <ShieldAlert size={24} color="var(--warning)" />
            <div>
              <p style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '0.25rem' }}>SIN-4082 (Cerrado)</p>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Reporte de choque leve en 2024. Cobertura ejecutada sin deducible adicional.</p>
            </div>
          </div>

        </div>
        
        {/* Footer Actions */}
        <div style={{ padding: '1.5rem', borderTop: '1px solid var(--border)', backgroundColor: 'var(--bg-color)', display: 'flex', gap: '1rem' }}>
          {(user?.role === ROLES.ADMIN || user?.role === ROLES.EDITOR) && (
            <button className="btn" onClick={() => setShowEditModal(true)} style={{ flex: 1, justifyContent: 'center', border: '1px solid var(--border)' }}>Editar Info</button>
          )}
          <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} disabled={aiError === 'error' && !forceApproved}>Avanzar Estado</button>
        </div>
      </div>

      {showEditModal && (
        <LeadModal editData={client} onClose={() => { setShowEditModal(false); onClose(); }} />
      )}
    </>
  );
};

export default ClientDrawer;
